import {
  isSameHexColor,
  normalizeHex,
  resolveThemeColorTargets,
} from "./customPrimaryColor";

type ThemeColorMode = "light" | "dark";

/** 与 theme.css 默认 --btn-primary-text（= --text-inverse-100）一致 */
const DEFAULT_BTN_PRIMARY_TEXT_BY_MODE: Record<ThemeColorMode, string> = {
  light: "#FFFFFF",
  dark: "#1E2023",
};

const CUSTOM_BTN_PRIMARY_TEXT_STORAGE_KEY =
  "app-custom-btn-primary-text-colors";

export type CustomBtnPrimaryTextColors = Partial<
  Record<ThemeColorMode, string>
>;

export const getDefaultBtnPrimaryTextByMode = (mode: ThemeColorMode): string =>
  DEFAULT_BTN_PRIMARY_TEXT_BY_MODE[mode];

export const isDefaultBtnPrimaryTextColor = (
  mode: ThemeColorMode,
  color: string
) => isSameHexColor(color, getDefaultBtnPrimaryTextByMode(mode));

const parseStored = (raw: string | null): CustomBtnPrimaryTextColors => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as CustomBtnPrimaryTextColors;
    return {
      light: parsed.light ? normalizeHex(parsed.light) || undefined : undefined,
      dark: parsed.dark ? normalizeHex(parsed.dark) || undefined : undefined,
    };
  } catch {
    return {};
  }
};

export const readStoredCustomBtnPrimaryTextColors =
  (): CustomBtnPrimaryTextColors =>
    parseStored(localStorage.getItem(CUSTOM_BTN_PRIMARY_TEXT_STORAGE_KEY));

export const writeStoredCustomBtnPrimaryTextColor = (
  mode: ThemeColorMode,
  color: string | null
) => {
  const next = readStoredCustomBtnPrimaryTextColors();
  if (color) {
    next[mode] = color;
  } else {
    delete next[mode];
  }
  localStorage.setItem(
    CUSTOM_BTN_PRIMARY_TEXT_STORAGE_KEY,
    JSON.stringify(next)
  );
};

const writeBtnPrimaryTextVar = (
  target: HTMLElement,
  mode: ThemeColorMode,
  color: string
) => {
  target.style.setProperty(`--custom-btn-primary-text-${mode}`, color);
};

export const clearCustomBtnPrimaryTextCssVars = (
  mode: ThemeColorMode,
  element?: HTMLElement
) => {
  resolveThemeColorTargets(element).forEach((target) => {
    target.style.removeProperty(`--custom-btn-primary-text-${mode}`);
  });
};

export const applyBtnPrimaryTextColor = (
  mode: ThemeColorMode,
  color: string,
  element?: HTMLElement
) => {
  resolveThemeColorTargets(element).forEach((target) => {
    writeBtnPrimaryTextVar(target, mode, color);
  });
};

export const readAppliedBtnPrimaryTextColor = (mode: ThemeColorMode) => {
  const stored = readStoredCustomBtnPrimaryTextColors()[mode];
  if (stored) return stored;
  return getDefaultBtnPrimaryTextByMode(mode);
};

export const restoreStoredCustomBtnPrimaryTextColors = (
  element?: HTMLElement
) => {
  const stored = readStoredCustomBtnPrimaryTextColors();
  resolveThemeColorTargets(element).forEach((target) => {
    (["light", "dark"] as const).forEach((mode) => {
      const color = stored[mode];
      if (color) writeBtnPrimaryTextVar(target, mode, color);
    });
  });
};
